import { type ReactNode, useState } from 'react';
import {
  ControlledUserSelectDialog,
  type ControlledUserSelectDialogProps,
} from '../components/controlled-user-select-dialog';
import { useUser } from '../context/user.context';

type ControlledUserSelectOptions = Pick<ControlledUserSelectDialogProps, 'onBeforeChange' | 'onChanged'>;

interface ControlledUserSelectResult {
  /** Superuser-gated handler for UserMenu's Select User item; `undefined` hides the item. */
  onSelectUser?: () => void;
  /** The controlled-user dialog, ready to render. `null` for non-superusers. */
  dialog: ReactNode;
}

/**
 * Self-contained controlled-user switching for superusers: owns the dialog and its open state and
 * gates the entry point on the JWT `superuser` claim. The JWT identity stays superuser while
 * controlling another user, whereas profile-derived flags reflect the controlled user, so the
 * gate must read the JWT for a controlling superuser to be able to switch or stop.
 */
export const useControlledUserSelect = (options?: ControlledUserSelectOptions): ControlledUserSelectResult => {
  const { state } = useUser();
  const [open, setOpen] = useState(false);

  const isSuperUser = state.user?.superuser === 'true';

  return {
    onSelectUser: isSuperUser ? () => setOpen(true) : undefined,
    dialog: isSuperUser ? <ControlledUserSelectDialog open={open} onOpenChange={setOpen} {...options} /> : null,
  };
};
